import { BannerPaymentFailed } from "@/components/billing/banner-payment-failed";
import { CardBillingEmail } from "@/components/billing/card-billing-email";
import { CardBillingTrial } from "@/components/billing/card-billing-trial";
import { CardInventoryError } from "@/components/billing/card-inventory-error";
import {
  CardLicenseInventory,
  CardLicenseInventorySkeleton,
} from "@/components/billing/card-license-inventory";
import { DialogLicenseCancelConfirm } from "@/components/billing/dialog-license-cancel-confirm";
import { DialogLicenseManagement } from "@/components/billing/dialog-license-management";
import { DialogLicenseResumeConfirm } from "@/components/billing/dialog-license-resume-confirm";
import ErrorMessage from "@/components/error-message";
import PageSkeleton from "@/components/page-skeleton";
import { CardApiCreditsSubscription } from "@/components/api-credits/card-api-credits-subscription";
import DialogSendInvitation from "@/components/team/dialog-send-invitation";
import { Card, CardContent } from "@/components/ui/card";
import { useToastError } from "@/components/ui/use-toast";
import { useUsers } from "@/contexts/users-provider";
import { useApi } from "@/hooks/useApi";
import { useTeamLicenseInventory } from "@/hooks/use-team-license-inventory";
import { useTranslation } from "@/hooks/useTranslation";
import {
  TeamBillingDTO,
  TeamInvitationDTO,
  TeamLicenseInventoryDTO,
} from "@shaarp/common";
import { CreditCardIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

type LicenseDialog = "monthly" | "annual" | null;

export default function TeamBillingPage() {
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { users, apiFetchUsers } = useUsers();

  const [teamBilling, setTeamBilling] = useState<TeamBillingDTO>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const {
    inventory,
    isLoading: inventoryLoading,
    error: inventoryError,
    refetch: fetchInventory,
    setInventory,
  } = useTeamLicenseInventory();
  const [invitations, setInvitations] = useState<TeamInvitationDTO[]>([]);

  const [openDialogInvitation, setOpenDialogInvitation] = useState(false);
  const [invitationPriceId, setInvitationPriceId] = useState<string>("");
  const [manageDialog, setManageDialog] = useState<LicenseDialog>(null);
  const [cancelDialog, setCancelDialog] = useState<LicenseDialog>(null);
  const [resumeDialog, setResumeDialog] = useState<LicenseDialog>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get<TeamBillingDTO>(`/my/team/billing`);
      setTeamBilling(data);
    } catch {
      toastError();
      setTeamBilling(undefined);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchInvitations = useCallback(async () => {
    try {
      const { data } = await api.get<TeamInvitationDTO[]>(
        `/my/team/invitations`
      );
      setInvitations(data);
    } catch {
      setInvitations([]);
    }
  }, []);

  // The inventory itself is fetched on mount by useTeamLicenseInventory.
  useEffect(() => {
    fetchData();
    fetchInvitations();
    apiFetchUsers();
  }, []);

  const onInvitationDialogClose = () => {
    setOpenDialogInvitation(false);
    setInvitationPriceId("");
    apiFetchUsers({ force: true });
    fetchInventory();
    fetchInvitations();
  };

  // A licence change can unassign users, so refresh members and the inventory
  // together once a switch/cancel/resume is confirmed.
  const handleInventoryUpdate = (updated: TeamLicenseInventoryDTO) => {
    setInventory(updated);
    setManageDialog(null);
    setCancelDialog(null);
    setResumeDialog(null);
    apiFetchUsers({ force: true });
    fetchInvitations();
  };

  const handleCancelApiSubscription = async () => {
    try {
      await api.post("/my/team/api-credits/subscription/cancel");
      const { data } = await api.get<TeamBillingDTO>("/my/team/billing");
      setTeamBilling(data);
    } catch {
      toastError();
    }
  };

  const handleResumeApiSubscription = async () => {
    try {
      await api.post("/my/team/api-credits/subscription/resume");
      const { data } = await api.get<TeamBillingDTO>("/my/team/billing");
      setTeamBilling(data);
    } catch {
      toastError();
    }
  };

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (!teamBilling) {
    return <ErrorMessage />;
  }

  return (
    <>
      {teamBilling.billingError === "payment_failed" && <BannerPaymentFailed />}

      {inventory ? (
        <CardLicenseInventory
          inventory={inventory}
          freePlan={inventory.freePlan}
          users={users}
          invitations={invitations}
          onInvite={(priceId) => {
            setInvitationPriceId(priceId);
            setOpenDialogInvitation(true);
          }}
          onManageMonthly={() => setManageDialog("monthly")}
          onManageAnnual={() => setManageDialog("annual")}
          onCancelMonthly={() => setCancelDialog("monthly")}
          onCancelAnnual={() => setCancelDialog("annual")}
          onResumeMonthly={() => setResumeDialog("monthly")}
          onResumeAnnual={() => setResumeDialog("annual")}
          onSwitchConfirmed={handleInventoryUpdate}
        />
      ) : inventoryLoading ? (
        <CardLicenseInventorySkeleton />
      ) : inventoryError ? (
        <CardInventoryError onRetry={fetchInventory} />
      ) : null}

      <CardBillingEmail billing={teamBilling} />

      {teamBilling.trialEndsAt && <CardBillingTrial billing={teamBilling} />}

      {/* API Credits Subscription */}
      {teamBilling.apiCreditsSubscription ? (
        <CardApiCreditsSubscription
          subscription={teamBilling.apiCreditsSubscription}
          compact
          onChangePlan={() => navigate("/integrations/api")}
          onCancel={handleCancelApiSubscription}
          onResume={handleResumeApiSubscription}
        />
      ) : teamBilling.apiCreditsBalance !== null &&
        teamBilling.apiCreditsBalance > 0 ? (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-majorelle/10">
                  <CreditCardIcon className="w-4 h-4 text-majorelle" />
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {t("team.apiCredits.billing.title")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("team.apiCredits.billing.balance", {
                      count: teamBilling.apiCreditsBalance,
                    })}
                  </p>
                </div>
              </div>
              <NavLink
                to="/integrations/api"
                className="text-sm font-medium text-majorelle hover:underline"
              >
                {t("team.apiCredits.billing.manage")}
              </NavLink>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="p-5">
          <div className="text-sm text-muted-foreground text-center">
            {t("team.billing.needHelp")}{" "}
            <a href="mailto:support@shaarp.ai" target="_blank">
              support@shaarp.ai
            </a>
          </div>
        </CardContent>
      </Card>

      {openDialogInvitation && (
        <DialogSendInvitation
          onClose={onInvitationDialogClose}
          inventory={inventory}
          defaultPriceId={invitationPriceId}
        />
      )}

      {manageDialog && inventory && (
        <DialogLicenseManagement
          frequency={manageDialog}
          inventory={inventory}
          users={users}
          invitations={invitations}
          onClose={() => setManageDialog(null)}
          onConfirm={handleInventoryUpdate}
          onRequestCancel={() => {
            setCancelDialog(manageDialog);
            setManageDialog(null);
          }}
        />
      )}

      {cancelDialog && inventory && (
        <DialogLicenseCancelConfirm
          frequency={cancelDialog}
          freqData={
            cancelDialog === "monthly" ? inventory.monthly : inventory.annual
          }
          onClose={() => setCancelDialog(null)}
          onConfirmed={handleInventoryUpdate}
        />
      )}

      {resumeDialog && inventory && (
        <DialogLicenseResumeConfirm
          frequency={resumeDialog}
          freqData={
            resumeDialog === "monthly" ? inventory.monthly : inventory.annual
          }
          onClose={() => setResumeDialog(null)}
          onConfirmed={handleInventoryUpdate}
        />
      )}
    </>
  );
}
